import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { Button } from './ui/button';
import { PushNotificationToggle } from './PushNotificationToggle';
import { NotificationBell } from './NotificationBell';
import { ThemeToggle } from './ThemeToggle';
import { HelpLauncher } from './HelpLauncher';
import { useCommissionToasts } from '../hooks/useCommissionToasts';
import { VerifyEmailBanner } from './VerifyEmailBanner';
import {
  LayoutDashboard,
  Mail,
  Users,
  Wallet,
  ShoppingBag,
  Tag,
  Settings,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronRight,
  TrendingUp,
  DollarSign,
  PieChart,
  Image,
  Home,
  Shield,
  HelpCircle,
  Activity,
  BarChart3,
  Store,
  CreditCard,
  Coins,
  Award,
  BookOpen,
  Briefcase,
  Tags,
  ShieldAlert,
  ScanLine,
  Sparkles,
  Receipt,
  MessageSquare,
  Upload
} from 'lucide-react';

const LOGO_ICON_FALLBACK = "/api/uploads/images/aga-icon-fallback.png";

export function AdminSidebarLayout({ children }) {
  const { user, logout, api } = useAuth();
  const { iconUrl, siteName } = useSiteConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [allowedModules, setAllowedModules] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [badges, setBadges] = useState({}); // { verification_failures: 3, withdrawals: 2 }
  const LOGO_ICON = iconUrl || LOGO_ICON_FALLBACK;

  useEffect(() => {
    if (user) {
      api.get('/api/auth/me/permissions').then(res => {
        setAllowedModules(res.data.modules || []);
      }).catch(() => {
        setAllowedModules(user.role === 'admin' ? null : ['dashboard']);
      }).finally(() => {
        setPermissionsLoaded(true);
      });
    }
  }, [user, api]);

  // Poll pending counts for the sidebar badges (verification failures + withdrawals).
  // Lightweight call (~50ms); refreshes every 30s while the admin tab is focused.
  useEffect(() => {
    if (!user || user.role !== 'admin' && !(allowedModules || []).some(m => ['verification_failures', 'withdrawals'].includes(m))) return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const { data } = await api.get('/api/admin/sidebar-badges');
        if (!cancelled) setBadges(data || {});
      } catch { /* silent */ }
    };
    fetchBadges();
    const id = setInterval(fetchBadges, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, api, allowedModules]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Module key to route mapping
  const moduleRouteMap = {
    'dashboard': '/admin',
    'users': '/admin/users',
    'portfolio': '/admin/portfolio',
    'withdrawals': '/admin/withdrawals',
    'reports': '/admin/reports',
    'services': '/admin/services',
    'plans': '/admin/plans',
    'cms': '/admin/cms',
    'media': '/admin/media',
    'analytics': '/admin/analytics',
    'roles': '/admin/roles',
    'merchants': '/admin/merchants',
    'settings': '/admin/settings',
    'marketplace_categories': '/admin/marketplace/categories',
    'industries': '/admin/industries',
    'audit_logs': '/admin/audit-logs',
    'verification_failures': '/admin/verification-failures',
  };

  const allMenuItems = [
    {
      category: 'Dashboard',
      items: [
        { href: '/admin', label: 'Overview', icon: LayoutDashboard, module: 'dashboard' },
      ]
    },
    {
      category: 'Management',
      items: [
        { href: '/admin/users', label: 'Users', icon: Users, module: 'users' },
        { href: '/admin/members/batch-upload', label: 'Batch Upload Members', icon: Upload, module: 'users' },
        { href: '/admin/verification-failures', label: 'Verification Failures', icon: ShieldAlert, module: 'verification_failures' },
        { href: '/admin/support', label: 'Support Inbox', icon: MessageSquare, module: 'support' },
        { href: '/admin/portfolio', label: 'Portfolio', icon: PieChart, module: 'portfolio' },
        { href: '/admin/stock-certificates', label: 'Stock Certificates', icon: Award, module: 'portfolio' },
        { href: '/admin/withdrawals', label: 'Withdrawals', icon: DollarSign, module: 'withdrawals' },
        { href: '/admin/reports', label: 'Reports', icon: BarChart3, module: 'reports' },
      ]
    },
    {
      category: 'Merchant Management',
      items: [
        { href: '/admin/merchants', label: 'Merchant Partners', icon: Store, module: 'merchants' },
        { href: '/admin/industries', label: 'Industries', icon: Briefcase, module: 'industries' },
        { href: '/admin/marketplace/categories', label: 'Marketplace Categories', icon: Tags, module: 'marketplace_categories' },
      ]
    },
    {
      category: 'Content',
      items: [
        { href: '/admin/plans', label: 'Plans', icon: Tag, module: 'plans' },
        { href: '/admin/cms', label: 'CMS Pages', icon: FileText, module: 'cms' },
        { href: '/admin/media', label: 'Media Library', icon: Image, module: 'media' },
        { href: '/admin/training', label: 'Training Manuals', icon: BookOpen, module: 'settings' },
      ]
    },
    {
      category: 'Configuration',
      items: [
        { href: '/admin/transactions', label: 'Scan Analytics', icon: ScanLine, module: 'analytics' },
        { href: '/admin/analytics', label: 'Analytics', icon: TrendingUp, module: 'analytics' },
        { href: '/admin/marketing', label: 'Marketing Autopilot', icon: Sparkles, module: 'settings' },
        { href: '/admin/email-health', label: 'Email Health', icon: Mail, module: 'settings' },
        { href: '/admin/roles', label: 'Roles & Access', icon: Shield, module: 'roles' },
        { href: '/admin/settings', label: 'Site Settings', icon: Settings, module: 'settings' },
        { href: '/admin/audit-logs', label: 'Audit Logs', icon: Activity, module: 'audit_logs' },
      ]
    }
  ];

  // Filter menu items based on role permissions — wait for permissions to load to prevent flash
  const menuItems = !permissionsLoaded
    ? [] // Don't render items until permissions are resolved
    : allowedModules === null
      ? allMenuItems // null = admin, show all
      : allMenuItems
          .map(cat => ({
            ...cat,
            items: cat.items.filter(item => allowedModules.includes(item.module))
          }))
          .filter(cat => cat.items.length > 0);

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#000B18] flex">
      {/* Mobile sidebar toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 rounded-lg bg-[#041A3B] border border-white/10 text-white shadow-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        data-testid="admin-mobile-menu-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#041A3B] border-r border-white/10 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200 h-[100dvh] lg:h-screen`} data-testid="admin-sidebar">
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <Link to="/admin" className="flex items-center gap-3">
                <img src={LOGO_ICON} alt="AGA" className="h-10 w-10" />
                <div>
                  <span className="font-outfit font-semibold text-white text-lg">AGA Admin</span>
                  <p className="text-[#5C7599] text-xs">Management Panel</p>
                </div>
              </Link>
              <div className="lg:hidden">
                <NotificationBell />
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-6">
            {menuItems.map((group) => (
              <div key={group.category}>
                <p className="text-[#5C7599] text-xs font-medium uppercase tracking-wider mb-2 px-3">
                  {group.category}
                </p>
                <ul className="space-y-1">
                  {group.items.map((item) => {
                    const badgeCount = badges[item.module];
                    return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive(item.href)
                            ? 'bg-[#00E5FF]/10 text-[#00E5FF]'
                            : 'text-[#A0B4CC] hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid={`sidebar-link-${item.module || item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="flex-1">{item.label}</span>
                        {badgeCount > 0 && (
                          <span
                            className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF1744] text-white text-[10px] font-bold flex items-center justify-center"
                            data-testid={`sidebar-badge-${item.module}`}
                          >
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                        {isActive(item.href) && !badgeCount && (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </Link>
                    </li>
                  );})}
                </ul>
              </div>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#00E5FF]/10 flex items-center justify-center">
                <span className="text-[#00E5FF] font-semibold">{user?.name?.[0] || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-[#5C7599] text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <PushNotificationToggle />
            <ThemeToggle className="w-full justify-center mt-2" />
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-[#FF1744] hover:bg-[#FF1744]/10 mt-2"
              data-testid="sidebar-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 lg:ml-0 min-h-screen pt-16 lg:pt-0">
        {children}
      </main>
      <HelpLauncher />
    </div>
  );
}

export function MemberSidebarLayout({ children }) {
  const { user, logout, api } = useAuth();
  const { iconUrl, siteName } = useSiteConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const badges = {}; // members don't have sidebar badges (kept for shared render block compatibility)
  const LOGO_ICON = iconUrl || LOGO_ICON_FALLBACK;
  // Listen for real-time referral commission events and fire celebratory toasts.
  useCommissionToasts();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isMerchant = user?.role === 'merchant';
  const isMRO = user?.role === 'mro';
  // Dual-access: a member who applied + got approved as a merchant partner,
  // OR a merchant who also subscribed to a membership plan. Both can switch
  // freely between Member Mode and Merchant Mode.
  const hasMerchantAccess = isMerchant || !!user?.is_merchant_partner;
  const isSubscribedMember = user?.subscription_status === 'active';
  const hasMemberAccess = (user?.role === 'member' && isSubscribedMember) || (isMerchant && isSubscribedMember);
  const hasDualAccess = hasMerchantAccess && hasMemberAccess;
  // `active_view` lets a dual-access user pick which sidebar/dashboard renders.
  // Falls back to the user's primary role when not yet set.
  const activeView = user?.active_view || (isMerchant ? 'merchant' : 'member');
  // `isMerchant` (used for nav rendering below) now respects the active view:
  // a member-merchant-partner currently in 'merchant' view gets the merchant
  // sidebar; a primary merchant who flipped to 'member' view gets the member
  // sidebar.
  // Rebind for downstream usage.
   
  const _origIsMerchant = isMerchant;
  const isMerchantNav = hasMerchantAccess && activeView === 'merchant';
  const planLabel = isSubscribedMember ? 'My Plan' : 'Subscribe';
  const memberMenuItems = [
    {
      category: 'My Account',
      items: [
        { href: '/wallet', label: 'Wallet', icon: Wallet },
        { href: '/referrals', label: 'Referrals', icon: Users },
        { href: '/membership-card', label: 'Membership Card', icon: CreditCard },
        { href: '/stocks', label: 'Stock Certificate', icon: Award },
        { href: '/my-discounts', label: 'My Discounts', icon: Tag },
      ]
    },
    {
      category: 'Services',
      items: [
        { href: '/merchant-partners', label: 'Marketplace', icon: Store },
        { href: '/my-plan', label: planLabel, icon: TrendingUp },
      ]
    },
  ];

  const getDashboardItem = () => {
    if (isMRO) return { href: '/mro', label: 'Merchant Relations', icon: Users };
    if (isMerchantNav) return { href: '/merchant', label: 'My Business', icon: Store };
    return { href: '/dashboard', label: 'Overview', icon: LayoutDashboard };
  };

  const getDashboardCategory = () => {
    if (isMRO) return 'MRO';
    if (isMerchantNav) return 'Merchant';
    return 'Dashboard';
  };

  const menuItems = isMRO ? [
    {
      category: getDashboardCategory(),
      items: [getDashboardItem()]
    },
    {
      category: 'My Account',
      items: [
        { href: '/mro/commissions', label: 'My Commissions', icon: DollarSign },
      ]
    },
    {
      category: 'Support',
      items: [
        { href: '/profile', label: 'My Profile', icon: Shield },
        { href: '/support', label: 'Tickets & Disputes', icon: MessageSquare },
        { href: '/help', label: 'Help Center', icon: HelpCircle },
      ]
    }
  ] : isMerchantNav ? [
    // ── Merchants get a strictly merchant-flavoured menu by default. If
    //    they ALSO buy a subscription plan ("dual account"), the member
    //    benefits unlock — exactly like a normal member.
    {
      category: getDashboardCategory(),
      items: [getDashboardItem()]
    },
    {
      category: 'Scanner',
      items: [
        { href: '/merchant/scan', label: 'Scan Member QR', icon: ScanLine },
        { href: '/merchant/transactions', label: 'Transactions', icon: Receipt },
      ]
    },
    // Subscriber-only block: hidden until they activate a plan.
    ...(isSubscribedMember ? memberMenuItems : [{
      // Show a single "Subscribe" entry that points to /pricing so they can
      // upgrade. Avoids dead clicks on Wallet/Referrals/etc.
      category: 'Subscriber Perks',
      items: [
        { href: '/pricing', label: 'Subscribe for Benefits', icon: TrendingUp },
      ]
    }]),
    {
      category: 'Support',
      items: [
        { href: '/profile', label: 'My Profile', icon: Shield },
        { href: '/support', label: 'Tickets & Disputes', icon: MessageSquare },
        { href: '/help', label: 'Help Center', icon: HelpCircle },
      ]
    }
  ] : [
    // ── Members: full benefit menu ───────────────────────────────────────
    {
      category: getDashboardCategory(),
      items: [getDashboardItem()]
    },
    ...memberMenuItems,
    {
      category: 'Support',
      items: [
        { href: '/profile', label: 'My Profile', icon: Shield },
        { href: '/support', label: 'Tickets & Disputes', icon: MessageSquare },
        { href: '/help', label: 'Help Center', icon: HelpCircle },
      ]
    }
  ];

  const isActive = (href) => {
    if (href === '/dashboard' || href === '/merchant' || href === '/mro') return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#000B18] flex">
      {/* Mobile sidebar toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 rounded-lg bg-[#041A3B] border border-white/10 text-white shadow-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        data-testid="member-mobile-menu-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#041A3B] border-r border-white/10 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200 h-[100dvh] lg:h-screen`} data-testid="member-sidebar">
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo + Notification */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <Link to={isMRO ? '/mro' : isMerchantNav ? '/merchant' : '/dashboard'} className="flex items-center gap-3">
                <img src={LOGO_ICON} alt="AGA" className="h-10 w-10" />
                <div>
                  <span className="font-outfit font-semibold text-white text-lg">{isMRO ? 'AGA MRO' : isMerchantNav ? 'AGA Merchant' : 'All Global'}</span>
                  <p className="text-[#5C7599] text-xs">{isMRO ? 'Relations Officer' : isMerchantNav ? 'Business Portal' : 'Member Portal'}</p>
                </div>
              </Link>
              <div className="lg:hidden">
                <NotificationBell />
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-6">
            {menuItems.map((group) => (
              <div key={group.category}>
                <p className="text-[#5C7599] text-xs font-medium uppercase tracking-wider mb-2 px-3">
                  {group.category}
                </p>
                <ul className="space-y-1">
                  {group.items.map((item) => {
                    const badgeCount = badges[item.module];
                    return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive(item.href)
                            ? 'bg-[#00E5FF]/10 text-[#00E5FF]'
                            : 'text-[#A0B4CC] hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid={`sidebar-link-${item.module || item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="flex-1">{item.label}</span>
                        {badgeCount > 0 && (
                          <span
                            className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF1744] text-white text-[10px] font-bold flex items-center justify-center"
                            data-testid={`sidebar-badge-${item.module}`}
                          >
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                        {isActive(item.href) && !badgeCount && (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </Link>
                    </li>
                  );})}
                </ul>
              </div>
            ))}
          </nav>

          {/* View switcher (only when user has dual access) */}
          {hasDualAccess && (
            <ViewSwitcher activeView={activeView} navigate={navigate} api={api} />
          )}

          {/* Subscription status */}
          <div className="p-4 border-t border-white/10">
            <div className={`p-3 rounded-lg ${user?.subscription_status === 'active' ? 'bg-[#00E676]/10 border border-[#00E676]/30' : 'bg-[#5C7599]/10 border border-white/10'}`}>
              <p className="text-xs text-[#A0B4CC] mb-1">Subscription</p>
              <p className={`font-medium ${user?.subscription_status === 'active' ? 'text-[#00E676]' : 'text-[#5C7599]'}`}>
                {user?.subscription_status === 'active' ? 'Active Member' : 'Not Subscribed'}
              </p>
            </div>
          </div>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#00E5FF]/10 flex items-center justify-center">
                <span className="text-[#00E5FF] font-semibold">{user?.name?.[0] || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-[#5C7599] text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <PushNotificationToggle />
            <ThemeToggle className="w-full justify-center mt-2" />
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-[#FF1744] hover:bg-[#FF1744]/10 mt-2"
              data-testid="member-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      
      <main className="flex-1 min-w-0 lg:ml-0 min-h-screen pt-16 lg:pt-0 pb-20 lg:pb-0">
        <VerifyEmailBanner />
        {children}
      </main>

      {/* Mobile sticky bottom nav — quick reach for key destinations */}
      <MemberMobileNav user={user} location={location} />
      <HelpLauncher />
    </div>
  );
}

/* ----------------------- Mobile bottom nav ----------------------- */
function MemberMobileNav({ user, location }) {
  const isMerchant = user?.role === 'merchant';
  const isMRO = user?.role === 'mro';
  const isSubscribedMember = user?.subscription_status === 'active';

  // Tailor the four most-frequent destinations per role
  const items = isMRO ? [
    { href: '/mro', label: 'Home', icon: LayoutDashboard },
    { href: '/mro/commissions', label: 'Earnings', icon: DollarSign },
    { href: '/support', label: 'Support', icon: MessageSquare },
    { href: '/profile', label: 'Profile', icon: Shield },
  ] : isMerchant ? (
    // Subscribed merchants get a Wallet quick-link in place of Transactions —
    // they're using the platform like a member too.
    isSubscribedMember ? [
      { href: '/merchant', label: 'Home', icon: LayoutDashboard },
      { href: '/merchant/scan', label: 'Scan', icon: ScanLine },
      { href: '/wallet', label: 'Wallet', icon: Wallet },
      { href: '/profile', label: 'Profile', icon: Shield },
    ] : [
      { href: '/merchant', label: 'Home', icon: LayoutDashboard },
      { href: '/merchant/scan', label: 'Scan', icon: ScanLine },
      { href: '/merchant/transactions', label: 'Txns', icon: Receipt },
      { href: '/profile', label: 'Profile', icon: Shield },
    ]
  ) : [
    { href: '/dashboard', label: 'Home', icon: Home },
    { href: '/wallet', label: 'Wallet', icon: Wallet },
    { href: '/marketplace', label: 'Marketplace', icon: Store },
    { href: '/referrals', label: 'Referrals', icon: Users },
  ];

  const tap = () => {
    // Light haptic — silently ignored on browsers without vibrate
    try { navigator.vibrate?.(10); } catch (_) {/* ignore */}
  };

  return (
    <div
      className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#000B18]/95 backdrop-blur-md border-t border-white/10 px-2 pb-[max(0.25rem,env(safe-area-inset-bottom))] pt-1.5"
      data-testid="member-mobile-nav"
    >
      <div className="flex items-center justify-around gap-1">
        {items.map(it => {
          const active = location.pathname === it.href || location.pathname.startsWith(it.href + '/');
          const Icon = it.icon;
          return (
            <Link
              key={it.href}
              to={it.href}
              onClick={tap}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-xl transition-all active:scale-95 ${
                active
                  ? 'text-[#00E5FF]'
                  : 'text-[#A0B4CC] hover:text-white'
              }`}
              data-testid={`mobile-nav-${it.label.toLowerCase()}`}
            >
              <div className={`p-1.5 rounded-xl transition-all ${active ? 'bg-[#00E5FF]/15' : ''}`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-medium leading-none">{it.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* ----------------------- Member/Merchant view switcher ----------------------- */
function ViewSwitcher({ activeView, navigate, api }) {
  const switchTo = async (view) => {
    if (view === activeView) return;
    try {
      const { toast } = await import('sonner');
      try {
        await api.post('/api/users/switch-view', { view });
        toast.success(`Switched to ${view === 'merchant' ? 'Merchant' : 'Member'} Mode`);
        navigate(view === 'merchant' ? '/merchant' : '/dashboard');
        // Force a reload so the AuthContext re-fetches the user and every
        // layout decision picks up the new active_view immediately.
        setTimeout(() => window.location.reload(), 200);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to switch view');
      }
    } catch {/* sonner unavailable — silent */}
  };
  return (
    <div className="px-4 pt-4 pb-2 border-t border-white/10" data-testid="view-switcher">
      <p className="text-[10px] uppercase tracking-wider text-[#5C7599] mb-2">View Mode</p>
      <div className="flex gap-1 rounded-lg bg-[#000B18]/60 p-1 border border-white/10">
        <button
          onClick={() => switchTo('member')}
          className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${
            activeView === 'member' ? 'bg-[#00E5FF] text-[#001627]' : 'text-[#A0B4CC] hover:text-white'
          }`}
          data-testid="view-switch-member"
        >
          Member
        </button>
        <button
          onClick={() => switchTo('merchant')}
          className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${
            activeView === 'merchant' ? 'bg-[#00E676] text-[#001627]' : 'text-[#A0B4CC] hover:text-white'
          }`}
          data-testid="view-switch-merchant"
        >
          Merchant
        </button>
      </div>
    </div>
  );
}

